import React, { useRef, useEffect, useState } from 'react'
import { createIframeHTML } from '../../utils/helpers'

/**
 * SafeHTMLRenderer - Renders HTML content in an isolated iframe
 * Provides complete security isolation from parent page context
 * Supports images, styling, and safe HTML while preventing XSS attacks
 */
const SafeHTMLRenderer = ({ 
  html, 
  title = 'Portfolio Company Update',
  className = '',
  style = {},
  onLoad = null,
  onError = null,
  allowImages = true,
  allowLinks = true,
  maxWidth = '100%'
}) => {
  const iframeRef = useRef(null)
  const [isLoading, setIsLoading] = useState(true)
  const [hasError, setHasError] = useState(false)

  // Create the HTML content for the iframe
  const iframeContent = createIframeHTML(html, {
    title,
    allowImages,
    allowLinks,
    maxWidth
  })

  // Handle iframe load events
  useEffect(() => {
    const iframe = iframeRef.current
    if (!iframe) return

    const handleLoad = () => {
      setIsLoading(false)
      setHasError(false)
      if (onLoad) onLoad()
    }

    const handleError = () => {
      setIsLoading(false)
      setHasError(true)
      if (onError) onError()
    }

    iframe.addEventListener('load', handleLoad)
    iframe.addEventListener('error', handleError)

    return () => {
      iframe.removeEventListener('load', handleLoad)
      iframe.removeEventListener('error', handleError)
    }
  }, [onLoad, onError])

  // Update iframe content when HTML changes
  useEffect(() => {
    const iframe = iframeRef.current
    if (!iframe) return

    try {
      // Create a blob URL for the HTML content
      const blob = new Blob([iframeContent], { type: 'text/html' })
      const url = URL.createObjectURL(blob)
      
      iframe.src = url
      setIsLoading(true)
      setHasError(false)

      // Clean up the blob URL when component unmounts or content changes
      return () => {
        URL.revokeObjectURL(url)
      }
    } catch (error) {
      console.error('Error creating iframe content:', error)
      setHasError(true)
      setIsLoading(false)
    }
  }, [iframeContent])

  // Fallback content for errors
  if (hasError) {
    return (
      <div className={`bg-gray-50 border border-gray-200 rounded-lg p-4 ${className}`} style={style}>
        <div className="text-center text-gray-500">
          <div className="mb-2">⚠️</div>
          <div className="text-sm">Unable to display content safely</div>
          <div className="text-xs mt-1">Content may contain unsafe elements</div>
        </div>
      </div>
    )
  }

  return (
    <div className={`relative ${className}`} style={style}>
      {/* Loading indicator */}
      {isLoading && (
        <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
          <div className="text-gray-500 text-sm">Loading content...</div>
        </div>
      )}
      
      {/* Iframe with security attributes */}
      <iframe
        ref={iframeRef}
        className="w-full border-0"
        style={{
          minHeight: '200px',
          height: 'inherit',
          width: '100%',
          border: 'none',
          borderRadius: '8px'
        }}
        sandbox="allow-same-origin"
        title={title}
        loading="lazy"
        // Security: Prevent iframe from accessing parent
        allow=""
        // Additional security headers would be set by server
      />
    </div>
  )
}

export default SafeHTMLRenderer
